import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import GoalQuestions from "./GoalQuestions_new";
import { useQuiz } from "../contexts/QuizContext";
import RateLimitModal from "./RateLimitModal";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const QUIZ_RATE_LIMIT_SUPPORT_MESSAGE =
  "You've reached the current limit for generating practice quizzes. Please wait a few minutes before trying again or contact support if you need a higher limit.";

const levelToColor = {
  Beginner: "bg-orange-100 text-orange-700",
  Developing: "bg-orange-100 text-orange-700",
  Proficient: "bg-green-100 text-green-700",
  Advanced: "bg-green-100 text-green-700",
  Mastery: "bg-purple-100 text-purple-700",
};

const barColor = {
  Beginner: "bg-orange-400",
  Developing: "bg-orange-400",
  Proficient: "bg-teal-500",
  Advanced: "bg-teal-500",
  Mastery: "bg-purple-500",
};

const getLevelWidth = (level) => {
  const map = {
    Beginner: 27,
    Developing: 40,
    Proficient: 60,
    Advanced: 80,
    Mastery: 100,
  };
  return map[level] || 0;
};

const GrowthAreas = () => {
  const navigate = useNavigate();
  const [growthAreas, setGrowthAreas] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [practiceLoadingKey, setPracticeLoadingKey] = useState(null);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const { saveQuizState, resetQuizState } = useQuiz();
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);

  useEffect(() => {
    const fetchGrowthAreas = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${baseUrl}/progress/progress2/growth-areas`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const formatted = [];
        const data = res.data.growthAreas || {};

        //bool check for if subject has no topics
        let hasNoTopics = true;
        for (const subject in data) {
          for (const topic in data[subject]) {
            //bool make true if atleast one topic is present
            hasNoTopics = false;
            formatted.push({
              title: topic,
              subject: subject,
              level: data[subject][topic].level,
              subtopics: Object.entries(data[subject][topic].subtopics).map(
                ([name, level]) => ({ name, level })
              ),
            });
          }
        }

        // if hasNoTopics = true then setGrowthAreas with subject but have title, level subtopics as null.
        if (hasNoTopics) {
          for (const subject in data) {
            formatted.push({
              title: null,
              subject: subject,
              level: null,
              subtopics: null,
            });
          }
        }

        const subjects = [...new Set(formatted.map((area) => area.subject))];
        setGrowthAreas(formatted);
        setSelectedSubject((prev) => {
          if (prev && subjects.includes(prev)) {
            return prev;
          }
          return subjects[0] || "";
        });
      } catch (error) {
        console.error("Error fetching growth areas:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGrowthAreas();
  }, []);

  // Group growth areas by subject
  const groupedAreas = growthAreas.reduce((acc, area) => {
    if (!acc[area.subject]) {
      acc[area.subject] = [];
    }
    acc[area.subject].push(area);
    return acc;
  }, {});

  const PRACTICE_TOTAL_QUESTIONS = 10;
  const PRACTICE_QUESTION_TYPE = "mcq";

  const buildPracticeFallbackQuestions = (subject, topic) => {
    const sampleOptions = ["Option A", "Option B", "Option C", "Option D"];
    return Array.from({ length: PRACTICE_TOTAL_QUESTIONS }, (_, i) => ({
      questionText: `Practice question ${i + 1} for ${topic} (${subject})`,
      options: sampleOptions,
      correctAnswer: 1,
      explanation: "Work through the concept to strengthen understanding.",
    }));
  };

  const normalizePracticeQuestions = (questions = []) =>
    questions
      .map((question, index) => {
        const optionSource = Array.isArray(question?.options)
          ? question.options
          : Array.isArray(question?.choices)
          ? question.choices
          : [];
        const options = optionSource
          .map((option) => {
            if (typeof option === "string") return option;
            if (option && typeof option === "object") {
              return (
                option.option_text ||
                option.label ||
                option.text ||
                option.value ||
                ""
              );
            }
            return "";
          })
          .filter(Boolean);
        const questionText =
          question?.questionText ||
          question?.question_text ||
          question?.prompt ||
          question?.question ||
          `Question ${index + 1}`;
        if (!questionText || !options.length) return null;
        const rawCorrect =
          typeof question?.correctAnswer === "number"
            ? question.correctAnswer
            : typeof question?.correct_option_index === "number"
            ? question.correct_option_index
            : typeof question?.answer_index === "number"
            ? question.answer_index
            : 0;
        const correctAnswer =
          typeof rawCorrect === "number" &&
          rawCorrect >= 0 &&
          rawCorrect < options.length
            ? rawCorrect
            : 0;
        const explanation =
          question?.explanation ||
          question?.reasoning ||
          question?.solution ||
          question?.answer_explanation ||
          "";
        return {
          questionText,
          options,
          correctAnswer,
          explanation,
        };
      })
      .filter(Boolean);

  const launchPracticeQuiz = (questions, subject, topic, metadata = null) => {
    const questionArray = Array.isArray(questions) ? questions : [];
    if (!questionArray.length) {
      toast.error("No questions available to start the practice quiz.");
      return;
    }
    const quizPayload =
      metadata && typeof metadata === "object"
        ? { ...metadata, questions: questionArray }
        : questionArray;
    saveQuizState({
      topicId: `growth-${subject}-${topic}`,
      questions: quizPayload,
      goalDetails: { subject, topic, current_level: 0 },
      currentQuestionIndex: 0,
      selectedOption: null,
      score: 0,
      showExplanation: false,
      quizCompleted: false,
      leveledUp: false,
      goalStatus: "",
      userAnswers: Array.from({ length: questionArray.length }, () => null),
    });
    setShowQuizModal(true);
  };

  const openPracticeQuiz = async (subject, areaTitle) => {
    const requestKey = `${subject}::${areaTitle}`;
    setPracticeLoadingKey(requestKey);
    const fallbackQuestions = buildPracticeFallbackQuestions(
      subject,
      areaTitle
    );
    try {
      if (!baseUrl) {
        toast.error("VITE_APP_BASE_URL is not configured.");
        launchPracticeQuiz(fallbackQuestions, subject, areaTitle);
        return;
      }

      const requestBody = {
        subject,
        chapter: areaTitle,
        num_questions: PRACTICE_TOTAL_QUESTIONS,
        question_type: PRACTICE_QUESTION_TYPE,
        level: "Intermediate",
      };

      const response = await fetch(`${baseUrl}/v2/quiz/generate`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        redirect: "follow",
        body: JSON.stringify(requestBody),
      });

      const rateLimitMessage = await extractRateLimitMessageFromResponse(
        response
      );
      if (rateLimitMessage) {
        const normalizedMessage =
          rateLimitMessage?.trim() || RATE_LIMIT_FALLBACK_MESSAGE;
        toast.warn(normalizedMessage);
        showRateLimitModal(normalizedMessage);
        return;
      }

      const responseText = await response.text();
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      let parsedData = {};
      if (responseText) {
        try {
          parsedData = JSON.parse(responseText);
        } catch (parseError) {
          console.error(
            "GrowthAreas | Failed to parse quiz generator response:",
            parseError
          );
          throw new Error(
            "Received invalid JSON from the quiz generator service."
          );
        }
      }

      const quizPayload =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;
      const questionList = Array.isArray(quizPayload?.questions)
        ? quizPayload.questions
        : [];
      if (!questionList.length) {
        throw new Error("API did not return any questions.");
      }

      const normalizedQuestions = normalizePracticeQuestions(questionList);
      if (!normalizedQuestions.length) {
        throw new Error("Unable to normalize generated questions.");
      }
      const metadata =
        quizPayload &&
        typeof quizPayload === "object" &&
        !Array.isArray(quizPayload)
          ? { ...quizPayload }
          : null;
      if (metadata) {
        delete metadata.questions;
      }
      launchPracticeQuiz(normalizedQuestions, subject, areaTitle, metadata);
    } catch (error) {
      console.error("GrowthAreas | Failed to generate practice quiz:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to generate practice quiz."
      );
      launchPracticeQuiz(fallbackQuestions, subject, areaTitle);
    } finally {
      setPracticeLoadingKey(null);
    }
  };

  const closePracticeQuiz = () => {
    setShowQuizModal(false);
    resetQuizState();
  };

  const subjects = Object.keys(groupedAreas);
  const selectedAreas = groupedAreas[selectedSubject] || [];
  const validAreas = selectedAreas.filter(
    (area) => area?.title && area?.level && area?.subtopics
  );
  const showEmptyState = validAreas.length === 0;

  if (loading) {
    return (
      <div className="min-h-screen p-6 font-sans bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D99FF] mb-4"></div>
          <p className="text-gray-600 text-lg">Loading growth areas...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen p-6 font-sans bg-gray-50">
        {subjects.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              No growth areas available yet
            </h2>
            <p className="text-gray-600 mb-6">
              Complete an assessment to discover where to focus next.
            </p>
            <button
              onClick={() => navigate("/assessment")}
              className="px-8 py-3 text-white rounded-2xl text-base font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
              style={{
                backgroundColor: "#FF6B00",
                boxShadow: "0 10px 15px -3px rgba(255, 107, 0, 0.25)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#E55A00";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#FF6B00";
              }}
            >
              Go to Assessment
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="w-full flex justify-center">
              <div className="flex gap-2 md:gap-3 overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0 justify-center items-center bg-gray-100 rounded-full">
                {subjects.map((subject) => (
                  <button
                    key={subject}
                    onClick={() => setSelectedSubject(subject)}
                    className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                      selectedSubject === subject
                        ? "bg-[#0D99FF] text-white shadow-md"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {subject}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <span className="mr-3">{selectedSubject}</span>
                  <span className="text-sm text-gray-500 font-normal">
                    ({validAreas.length}{" "}
                    {validAreas.length === 1 ? "topic" : "topics"})
                  </span>
                </h2>
              </div>
              <div className="p-6">
                {showEmptyState ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 text-lg mb-6">
                      Please set your level for the subject.
                    </p>
                    <button
                      onClick={() => navigate("/assessment")}
                      className="px-8 py-3 text-white rounded-2xl text-base font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                      style={{
                        backgroundColor: "#FF6B00",
                        boxShadow: "0 10px 15px -3px rgba(255, 107, 0, 0.25)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#E55A00";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#FF6B00";
                      }}
                    >
                      Go to Assessment
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {validAreas.map((area, idx) => {
                      const areaKey = `${selectedSubject}::${area.title}`;
                      return (
                        <div
                          key={`${area.title}-${idx}`}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {area.title}
                          </h3>

                          <div className="w-full h-6 bg-gray-200 rounded-full mb-3 overflow-hidden">
                            <div
                              className={`h-full text-white font-semibold text-center flex items-center justify-center ${
                                barColor[area.level]
                              } px-2`}
                              style={{
                                width: `${getLevelWidth(area.level)}%`,
                                fontSize: "clamp(10px, 2.5vw, 14px)",
                                minHeight: "24px",
                                padding: "0 8px",
                              }}
                            >
                              <span className="truncate">{area.level}</span>
                            </div>
                          </div>

                          <div className="flex justify-between text-sm text-gray-500 font-medium mb-3">
                            <span>Beginner</span>
                            <span>Advanced</span>
                            <span>Mastery</span>
                          </div>

                          <hr className="mb-3" />

                          <div>
                            <div className="w-full flex justify-center mt-6 mb-6">
                              {/* <button
                                type="button"
                                onClick={() =>
                                  openPracticeQuiz(selectedSubject, area.title)
                                }
                                disabled={practiceLoadingKey === areaKey}
                                className="px-4 py-2 rounded-full border border-[#0D99FF] text-[#0D99FF] bg-white text-sm font-semibold hover:bg-[#0D99FF]/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {practiceLoadingKey === areaKey
                                  ? "Generating..."
                                  : "Practice to achieve mastery"}
                              </button> */}
                            </div>
                            {area.subtopics.map((sub, i) => (
                              <div
                                key={i}
                                className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                              >
                                <span className="text-sm text-gray-800">
                                  {sub.name}
                                </span>
                                <span
                                  className={`text-sm font-semibold px-3 py-1 rounded-full ${
                                    levelToColor[sub.level]
                                  }`}
                                >
                                  {sub.level}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {showQuizModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              aria-hidden="true"
            />
            <div className="relative w-[98vw] h-[98vh] max-w-none rounded-2xl bg-white p-0 shadow-xl overflow-hidden">
              <div className="h-full">
                <GoalQuestions embedded onClose={closePracticeQuiz} />
              </div>
            </div>
          </div>
        )}
      </div>
      <RateLimitModal
        isOpen={isRateLimitModalOpen}
        serverMessage={rateLimitServerMessage}
        supportMessage={QUIZ_RATE_LIMIT_SUPPORT_MESSAGE}
        onClose={handleRateLimitModalClose}
      />
    </>
  );
};

export default GrowthAreas;
